import { useState, useEffect, createContext, useContext } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, LogOut, Package, ClipboardList, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getAdminToken() {
  return localStorage.getItem("clearpath_admin_token");
}

function clearAdminToken() {
  localStorage.removeItem("clearpath_admin_token");
}

async function adminFetch(url, options = {}) {
  const token = getAdminToken();
  const headers = { ...options.headers, Authorization: `Bearer ${token}` };
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401 || res.status === 403) {
    clearAdminToken();
    window.location.href = "/admin";
    throw new Error("Session expired");
  }
  return res;
}

const AuthContext = createContext(null);

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        toast.error("Invalid password");
        return;
      }
      const data = await res.json();
      localStorage.setItem("clearpath_admin_token", data.token);
      toast.success("Logged in");
      onLogin();
    } catch {
      toast.error("Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
          <div className="flex items-center justify-center mb-6">
            <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
              <Lock className="w-6 h-6 text-sky-600" />
            </div>
          </div>
          <h1
            className="text-2xl font-bold text-slate-900 text-center mb-2"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="admin-login-headline"
          >
            Admin Login
          </h1>
          <p className="text-sm text-slate-500 text-center mb-6">
            ClearPath Rapid Diagnostics
          </p>
          <form onSubmit={handleLogin}>
            <Label htmlFor="password" className="text-slate-700 text-sm font-medium">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 mb-4"
              placeholder="Enter admin password"
              data-testid="admin-password-input"
              autoFocus
            />
            <Button
              type="submit"
              className="w-full bg-sky-600 hover:bg-sky-700 text-white"
              disabled={loading || !password}
              data-testid="admin-login-btn"
            >
              {loading ? "Logging in..." : "Log In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function AdminShell({ onLogout }) {
  const location = useLocation();

  const handleLogout = () => {
    clearAdminToken();
    toast.success("Logged out");
    onLogout();
  };

  const navItems = [
    { path: "/admin/orders", label: "Orders", icon: ClipboardList },
    { path: "/admin/orders/new", label: "New Order", icon: Package },
    { path: "/admin/products", label: "Pricing", icon: DollarSign },
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-3 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="font-bold text-sm tracking-wide" style={{ fontFamily: "Manrope, sans-serif" }}>
              ClearPath Admin
            </span>
            <nav className="flex gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors ${
                      active
                        ? "bg-sky-600 text-white"
                        : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`}
                    data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>
      <Outlet />
    </div>
  );
}

export default function AdminLayout() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const navigate = useNavigate();

  if (!authed) {
    return (
      <AdminLogin
        onLogin={() => {
          setAuthed(true);
          navigate("/admin/orders");
        }}
      />
    );
  }

  return (
    <AdminShell
      onLogout={() => setAuthed(false)}
    />
  );
}

export { getAdminToken, clearAdminToken, adminFetch, BACKEND_URL as ADMIN_BACKEND_URL };
